import React, {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectSignedIn, signOut } from "reducers/auth";
import { Link } from "react-router-dom";
import { Transition } from "react-transition-group";
import { toast } from "react-toastify";
import { Scopes, useLoadScopes } from "helpers/auth";
import { HeaderAction } from "App";
import { APP_ROUTES, endpoints } from "config";
import { DISABLE_AUTO_SIGNIN_KEY } from "components/SignIn/SignIn.config";
import api from "api";
import { Avatar, AvatarImage, AvatarFallback } from "components/Avatar/Avatar";

type AccountIconProps = {
  setHeaderActions: React.Dispatch<React.SetStateAction<HeaderAction | null>>;
  headerActions: HeaderAction | null;
  entry?: string;
};

const AccountIcon: React.FC<AccountIconProps> = ({ ...props }) => {
  const isSignedIn = useSelector(selectSignedIn);

  const [profileInitials, setProfileInitials] = useState<string>("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const hasAttemptedRefresh = useRef(false);

  const [iconOptionsActive, setIconOptionsActive] = useState(false);

  const handleIconClick = () => {
    setIconOptionsActive(!iconOptionsActive);
    props.setHeaderActions(
      props.headerActions === "Account" ? null : "Account"
    );
  };

  const accessElements = useLoadScopes();

  const headerAccessElements = accessElements.filter(
    (item) =>
      item.hasOwnProperty("elementType") &&
      item.elementType === "HeaderNavigation"
  );

  useEffect(() => {
    const localStorageProfileInitials =
      window.localStorage.getItem("ProfileInitials");

    if (
      localStorageProfileInitials &&
      typeof localStorageProfileInitials === "string"
    ) {
      setProfileInitials(localStorageProfileInitials);
    }

    const localStoragePhotoUrl =
      window.localStorage.getItem("ProfilePhotoUrl");

    if (localStoragePhotoUrl) {
      setProfilePhotoUrl(localStoragePhotoUrl);
    }
  }, []);

  useEffect(() => {
    const handleProfilePhotoUpdated = () => {
      const url = window.localStorage.getItem("ProfilePhotoUrl");
      setProfilePhotoUrl(url);
      hasAttemptedRefresh.current = false;
    };

    window.addEventListener("profilePhotoUpdated", handleProfilePhotoUpdated);
    return () => {
      window.removeEventListener(
        "profilePhotoUpdated",
        handleProfilePhotoUpdated
      );
    };
  }, []);

  const dropdownRef = useRef<any>(null);
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIconOptionsActive(false);

      if (props.headerActions === "Account") props.setHeaderActions(null);
    }
  };
  useEffect(() => {
    if (props.headerActions !== "Account") return;
    document.addEventListener("click", handleClickOutside);
    return () => {
      if (props.headerActions !== "Account") return;
      document.removeEventListener("click", handleClickOutside);
    };
  }, [props.headerActions]);

  if (!isSignedIn) return null;

  return (
    <div className="AccountIconContainer" ref={dropdownRef}>
      <div className="AccountIcon" onClick={handleIconClick}>
        <Avatar size="xs">
          {profilePhotoUrl && (
            <AvatarImage
              src={profilePhotoUrl}
              alt={profileInitials}
              onError={() => {
                if (hasAttemptedRefresh.current) {
                  setProfilePhotoUrl(null);
                  localStorage.removeItem("ProfilePhotoUrl");
                  return;
                }
                hasAttemptedRefresh.current = true;
                api
                  .get(endpoints.profilePhoto.photo)
                  .then((response) => {
                    const newUrl = response.data?.details?.photoUrl;
                    if (newUrl) {
                      localStorage.setItem("ProfilePhotoUrl", newUrl);
                      setProfilePhotoUrl(newUrl);
                    } else {
                      localStorage.removeItem("ProfilePhotoUrl");
                      setProfilePhotoUrl(null);
                    }
                  })
                  .catch(() => {
                    localStorage.removeItem("ProfilePhotoUrl");
                    setProfilePhotoUrl(null);
                  });
              }}
            />
          )}
          <AvatarFallback
            initials={profileInitials}
            colorSeed={`${localStorage.getItem("FirstName") ?? ""} ${localStorage.getItem("LastName") ?? ""}`}
          />
        </Avatar>
      </div>

      <Transition
        // in={iconOptionsActive}
        in={props.headerActions === "Account"}
        timeout={50}
        mountOnEnter
        unmountOnExit
      >
        {/* <ClickOutsideDetector
          onClickOutside={() => {
            setIconOptionsActive(false);
            props.setHeaderActions(null);
          }}
        > */}
        <DropDownMenu
          headerAccessElements={headerAccessElements}
          setIconOptionsActive={setIconOptionsActive}
          setHeaderActions={props.setHeaderActions}
        />
        {/* </ClickOutsideDetector> */}
      </Transition>
    </div>
  );
};

export { AccountIcon };

type DropDownMenuProps = {
  headerAccessElements: Scopes;
  setIconOptionsActive: Dispatch<SetStateAction<boolean>>;
  setHeaderActions: React.Dispatch<React.SetStateAction<HeaderAction | null>>;
};

const DropDownMenu: React.FC<DropDownMenuProps> = ({
  headerAccessElements,
  setIconOptionsActive,
  setHeaderActions,
}) => {
  const dispatch = useDispatch();
  const handleSignOutClick = () => {
    toast.dismiss();

    // Set flag to disable auto sign-in until user manually logs in
    window.localStorage.setItem(DISABLE_AUTO_SIGNIN_KEY, "true");

    dispatch(signOut());

    setIconOptionsActive(false);
    setHeaderActions(null);
  };

  return (
    <div className="AccountIconOptionsContainer">
      <div className="AccountIconOptions">
        <Link
          to={APP_ROUTES.account}
          className="AccountIconOption"
          onClick={() => {
            setIconOptionsActive(false);
            setHeaderActions(null);
          }}
          key={`dropdown-link-profile`}
        >
          Account
        </Link>

        {headerAccessElements &&
          headerAccessElements.map((item, idx) => {
            if (item.path && item.displayName) {
              return (
                <Link
                  to={item.path}
                  className="AccountIconOption"
                  onClick={() => {
                    setIconOptionsActive(false);
                    setHeaderActions(null);
                  }}
                  key={`dropdown-link-${idx}`}
                >
                  {item.displayName}
                </Link>
              );
            } else {
              return null;
            }
          })}

        <span
          className="AccountIconOption"
          onClick={() => handleSignOutClick()}
          key="Sign out"
        >
          Sign out
        </span>
      </div>
    </div>
  );
};

type ClickOutsideDetectorProps = {
  onClickOutside?(): void;
};

const ClickOutsideDetector: React.FC<ClickOutsideDetectorProps> = ({
  onClickOutside,
  children,
}) => {
  return (
    <div>
      <div className="ClickOutsideDetector" onClick={onClickOutside} />
      {children}
    </div>
  );
};
